'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BlurUpImage } from '@/components/primitives/BlurUpImage';
import { Reveal } from '@/components/motion/Reveal';
import { SITE_COPY } from '@/lib/data/copy';
import type { PhotoKey, SiteCopy } from '@/lib/content';

export type Print = SiteCopy['photos'][PhotoKey];

/**
 * Three polaroid prints beside the headline: each photograph in a paper frame
 * with the deep bottom margin a polaroid has, a strip of tape holding it up,
 * a caption written on the margin by hand, tilted a few degrees, overlapping.
 * The composition follows the staggered collage on Kricket's home page; the
 * prints and the handwriting are ours.
 *
 * Click a print and the page dims while the print flies from where it hangs to
 * the centre of the screen, straightens, and shows its note. The flight is a
 * transform transition from the print's measured position to the centre.
 *
 * As the hero scrolls away each print drifts a little, in its own direction
 * (the `--drift` distance), so the group eases apart. That drift is a CSS
 * scroll-driven animation (see globals.css): no JavaScript, static where
 * unsupported, and off under reduced motion.
 *
 * The column is positioned over the hero rather than laid out beside the
 * text, so it can never make the hero taller: the prints take their size from
 * the hero's height and the column clips anything that drifts past its edges.
 *
 * Phones do not get it: the photo band directly under the hero carries the
 * picture there, and three prints beside a 40px headline have no room.
 */
const SLOTS: {
  /** Placement within the stage, and how far it drifts as the hero leaves. */
  place: string;
  /** Degrees. Applied to the print, and undone as it flies to the centre. */
  tilt: number;
  drift: string;
  delay: number;
}[] = [
  { place: 'bottom-0 left-0 h-[min(70cqh,54cqw)]', tilt: -3, drift: '-18%', delay: 350 },
  { place: 'top-0 left-[28%] z-10 h-[min(74cqh,57cqw)]', tilt: 2, drift: '10%', delay: 450 },
  { place: 'right-0 bottom-[8%] h-[min(66cqh,51cqw)]', tilt: 5, drift: '-26%', delay: 550 },
];

type Slot = (typeof SLOTS)[number];

/**
 * The frame itself, sized by its height. The photo is square, as a polaroid's
 * is; the margin below it is where the caption goes. `sizes` is the rendered
 * width so phones and the lightbox each get a sensible file.
 */
function Polaroid({
  photo,
  sizes,
  captionClass,
  tape = true,
}: {
  photo: Print;
  sizes: string;
  captionClass: string;
  tape?: boolean;
}) {
  // Spans throughout: the print is also the content of a <button>, which may
  // hold phrasing content only.
  return (
    <span className="relative flex h-full flex-col border border-ink/15 bg-paper p-[6%] pb-0">
      {tape && (
        <span
          aria-hidden="true"
          className="absolute -top-[4%] left-1/2 h-[6%] w-[36%] -translate-x-1/2 -rotate-3 border border-ink/10 bg-paper-2/80"
        />
      )}
      <span className="block aspect-square w-full overflow-hidden bg-dark">
        <BlurUpImage
          src={photo.image}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          sizes={sizes}
          className="size-full object-cover"
        />
      </span>
      <span className={`flex flex-1 items-center justify-center font-hand text-ink ${captionClass}`}>
        {photo.caption ?? ' '}
      </span>
    </span>
  );
}

function Lightbox({
  photo,
  tilt,
  from,
  onClose,
}: {
  photo: Print;
  tilt: number;
  from: DOMRect;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Fly the print from where it was clicked, tilted as it hung, to its resting
  // place in the centre. A Web Animations call rather than a CSS transition:
  // it needs no "before" style to have been painted, so it is immune to the
  // effect running twice in development, and it can be skipped outright for
  // reduced motion, leaving the print simply in place. `data-shown` on the
  // dialog fades the dim and the note in; it is set on the DOM directly so
  // nothing re-renders mid-flight.
  useLayoutEffect(() => {
    const el = printRef.current;
    const dialog = dialogRef.current;
    if (!el || !dialog) return;
    // Lock the page before measuring: where the scrollbar takes space, hiding
    // it widens the viewport and would move the centre after the sums.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const to = el.getBoundingClientRect();
    const scale = from.height / to.height;
    const dx = from.left + from.width / 2 - (to.left + to.width / 2);
    const dy = from.top + from.height / 2 - (to.top + to.height / 2);
    const flight =
      !reduce && typeof el.animate === 'function'
        ? el.animate(
            [
              { transform: `translate(${dx}px, ${dy}px) scale(${scale}) rotate(${tilt}deg)` },
              { transform: 'none' },
            ],
            { duration: 700, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
          )
        : null;
    const frame = requestAnimationFrame(() => dialog.setAttribute('data-shown', 'true'));
    return () => {
      cancelAnimationFrame(frame);
      flight?.cancel();
      document.body.style.overflow = overflow;
    };
  }, [from, tilt]);

  // While the print is up the rest of the page is inert: it cannot be tabbed
  // into, clicked or read, which is what makes this a modal rather than an
  // overlay. Only elements that were not already inert are restored.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const dialog = dialogRef.current;
    const madeInert = [...document.body.children].filter(
      (el) => el !== dialog && !el.hasAttribute('inert'),
    );
    for (const el of madeInert) el.setAttribute('inert', '');
    return () => {
      document.removeEventListener('keydown', onKey);
      for (const el of madeInert) el.removeAttribute('inert');
    };
  }, [onClose]);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      aria-describedby={photo.note ? 'hero-print-note' : undefined}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
    >
      {/* Clicking the dimmed page closes, as a convenience for the mouse; the
          keyboard has Escape and the Close button, so this is not a control. */}
      <div aria-hidden="true" onClick={onClose} className="lightbox-dim absolute inset-0 bg-ink/80" />
      <div className="relative flex flex-col items-center gap-7">
        <div
          ref={printRef}
          className="lightbox-print h-[min(62vh,560px)]"
          style={{ aspectRatio: '0.82' }}
        >
          <Polaroid photo={photo} sizes="min(51vh, 460px)" captionClass="text-[clamp(22px,3.2vh,34px)]" />
        </div>
        <div className="lightbox-dim flex flex-col items-center gap-6">
          {photo.note && (
            <p
              id="hero-print-note"
              className="max-w-[44ch] text-center font-display text-[19px] leading-[1.5] text-dark-soft"
            >
              {photo.note}
            </p>
          )}
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="label-xs text-dark-soft underline underline-offset-4 hover:text-paper focus-visible:outline-paper"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Three prints over the right of a section. `className` positions and pads the
 * column to match the section it sits on; the hero's values are the default.
 */
export function PrintCollage({
  photos,
  className = 'pt-[68px] pr-9 pb-[62px]',
}: {
  photos: readonly Print[];
  className?: string;
}) {
  const [open, setOpen] = useState<{ slot: Slot; photo: Print; from: DOMRect } | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const refocus = useRef(false);

  const close = useCallback(() => {
    refocus.current = true;
    setOpen(null);
  }, []);

  // Focus goes back to the print only after the lightbox has unmounted: until
  // its cleanup runs the collage is still inert and cannot take focus.
  useEffect(() => {
    if (open || !refocus.current) return;
    refocus.current = false;
    openerRef.current?.focus();
  }, [open]);

  return (
    <>
      <div
        className={`hero-collage absolute inset-y-0 right-0 hidden w-[40%] overflow-clip md:block ${className}`}
      >
        {/* The stage takes its width from the hero's height, so the cluster stays
            the same shape whether the window is a laptop or a wide monitor. It is
            a size container so the prints can follow whichever of its height or
            width is the tighter bound, which on a tablet is the width. */}
        <div className="relative ml-auto aspect-[4/3] h-full max-w-full [container-type:size]">
          {SLOTS.map((slot, i) => {
            const photo = photos[i];
            if (!photo) return null;
            return (
              <Reveal key={photo.image + i} delay={slot.delay} className={`absolute ${slot.place}`}>
                <div
                  className="collage-drift h-full"
                  style={{ '--drift': slot.drift } as React.CSSProperties}
                >
                  <button
                    type="button"
                    // Named by the description, not the caption: the caption is
                    // a flourish, the description says what the picture is.
                    aria-label={`Open photo: ${photo.alt}`}
                    onClick={(event) => {
                      openerRef.current = event.currentTarget;
                      setOpen({ slot, photo, from: event.currentTarget.getBoundingClientRect() });
                    }}
                    style={{ transform: `rotate(${slot.tilt}deg)`, aspectRatio: '0.82' }}
                    className="block h-full cursor-pointer text-left transition-transform duration-500 motion-safe:hover:scale-[1.03] focus-visible:outline-brick"
                  >
                    <Polaroid
                      photo={photo}
                      sizes="(min-width: 768px) 13vw, 1px"
                      captionClass="text-[clamp(14px,1.6vw,22px)]"
                    />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
      {/* Into the body, not the hero: a fixed dialog inside any transformed
          ancestor (the page's enter animation, a reveal) would be positioned
          against that ancestor rather than the screen. */}
      {open &&
        createPortal(
          <Lightbox photo={open.photo} tilt={open.slot.tilt} from={open.from} onClose={close} />,
          document.body,
        )}
    </>
  );
}

const HERO_PRINTS: readonly PhotoKey[] = ['heroLeft', 'heroMiddle', 'heroRight'];

export function HeroCollage() {
  return <PrintCollage photos={HERO_PRINTS.map((key) => SITE_COPY.photos[key])} />;
}
